/**
 * Hook for fetching and managing room data
 *
 * This hook provides an interface for components to access room data
 * and abstracts away the implementation details of data fetching.
 */

import { useCallback, useEffect, useState } from 'react';
import { roomService } from '../services';
import { Room, SearchFilters } from '../types';

interface UseRoomsResult {
    rooms: Room[];
    isLoading: boolean;
    error: Error | null;
    totalCount: number;
    currentPage: number;
    lastPage: number;
    fetchRooms: (filters?: SearchFilters) => Promise<void>;
    fetchRoomById: (id: number) => Promise<Room | null>;
    searchRooms: (query: string, filters?: Omit<SearchFilters, 'query'>) => Promise<void>;
}

/**
 * Hook for fetching and managing room data
 */
export function useRooms(initialFilters?: SearchFilters): UseRoomsResult {
    // State for room data and loading/error states
    const [rooms, setRooms] = useState<Room[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    // Pagination state
    const [totalCount, setTotalCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [lastPage, setLastPage] = useState<number>(1);

    /**
     * Fetch rooms based on optional filters
     */
    const fetchRooms = useCallback(async (filters?: SearchFilters) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await roomService.getRooms(filters);

            setRooms(response.data);
            setTotalCount(response.meta.total);
            setCurrentPage(response.meta.current_page);
            setLastPage(response.meta.last_page);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch rooms'));
            console.error('Error fetching rooms:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Fetch a single room by ID
     */
    const fetchRoomById = useCallback(async (id: number): Promise<Room | null> => {
        setIsLoading(true);
        setError(null);

        try {
            const room = await roomService.getRoom(id);
            return room;
        } catch (err) {
            setError(err instanceof Error ? err : new Error(`Failed to fetch room with ID: ${id}`));
            console.error(`Error fetching room with ID ${id}:`, err);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Search rooms by query string
     */
    const searchRooms = useCallback(async (query: string, filters?: Omit<SearchFilters, 'query'>) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await roomService.searchRooms(query, filters);

            setRooms(response.data);
            setTotalCount(response.meta.total);
            setCurrentPage(response.meta.current_page);
            setLastPage(response.meta.last_page);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to search rooms'));
            console.error('Error searching rooms:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch rooms on initial render if initialFilters are provided
    useEffect(() => {
        if (initialFilters) {
            fetchRooms(initialFilters);
        }
    }, [fetchRooms, initialFilters]);

    return {
        rooms,
        isLoading,
        error,
        totalCount,
        currentPage,
        lastPage,
        fetchRooms,
        fetchRoomById,
        searchRooms,
    };
}
